/**
 * Shared UI primitives for the two invite-a-spark surfaces. Pure
 * presentational pieces — the helpers/hook live in inviteSparkShared.ts
 * so this file is a clean component-only module (react-refresh).
 */

import { Box, Button, Flex, Spinner, Text, chakra } from "@chakra-ui/react";
import { MatchesPageSpark } from "@greenflagdate/shared";
import { useMediaBlob } from "../../lib/mediaHelpers";
import {
  primaryProfileImagePath,
  useMeasuredVariantPath,
} from "../../lib/profileMedia";
import { Icon } from "../Icon/Icon";
import { EligibleEvent } from "./inviteSparkShared";
import { formatEventWhen } from "../../lib/formatEventWhen";

export function Avatar({
  path,
  alt,
  size = "72px",
}: {
  path?: string;
  alt: string;
  size?: string;
}) {
  const [setMeasureRef, variantPath] = useMeasuredVariantPath(path);
  const { blob } = useMediaBlob(variantPath ?? "");
  const bg = blob ? `url(${URL.createObjectURL(blob)})` : undefined;
  return (
    <Box
      ref={setMeasureRef}
      aria-label={alt}
      w={size}
      h={size}
      borderRadius="full"
      bg={bg ? "bg.subtle" : "bg.brandSoft"}
      backgroundImage={bg}
      backgroundSize="cover"
      backgroundPosition="center"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="fg.brand"
    >
      {!bg && <Icon name="user-solid" boxSize="50%" />}
    </Box>
  );
}

/**
 * One person in a "pick one of your sparks" list — shared by the event page's
 * invite drawer and the post-registration invite prompt.
 *
 * A viewer may only have one active invite out to a given spark, so a spark
 * they've already invited can't be picked again: the row is disabled and says
 * where that invite went instead of silently doing nothing.
 *
 * `loading` / `disabled` exist for the surface where picking a row sends the
 * invite outright (the post-registration prompt): the picked row spins while
 * the send is in flight and its neighbours go unpickable, so one tap can't
 * become two invites.
 */
export function SparkPickerRow({
  spark,
  onPick,
  loading,
  disabled,
}: {
  spark: MatchesPageSpark;
  onPick: () => void;
  /** A send is in flight for THIS spark. */
  loading?: boolean;
  /** Not pickable right now — e.g. another row's send is in flight. */
  disabled?: boolean;
}) {
  const invited = spark.sentInvitation;
  const firstName = spark.profile.firstName || "match";
  const blocked = !!invited || !!disabled || !!loading;
  return (
    <chakra.button
      type="button"
      disabled={blocked}
      aria-disabled={blocked}
      aria-busy={loading}
      display="flex"
      alignItems="center"
      gap="0.75rem"
      w="100%"
      textAlign="left"
      bg="transparent"
      border="none"
      cursor={blocked ? "not-allowed" : "pointer"}
      opacity={invited || disabled ? 0.55 : 1}
      py="0.5rem"
      px="0.5rem"
      borderRadius="md"
      _hover={blocked ? undefined : { bg: "bg.subtle" }}
      onClick={blocked ? undefined : onPick}
    >
      <Avatar
        path={primaryProfileImagePath(spark.profile)}
        alt={firstName}
        size="48px"
      />
      <Box minW="0">
        <Text fontWeight="semibold" truncate>
          {spark.profile.firstName}
          {spark.profile.age ? `, ${spark.profile.age}` : ""}
        </Text>
        {invited && (
          <Text fontSize="xs" color="fg.muted" truncate>
            {invited.status === "accepted" ? "Going to" : "Invited to"}{" "}
            {invited.title || invited.activity} ·{" "}
            {formatEventWhen(invited.startsAt)}
          </Text>
        )}
      </Box>
      {loading && <Spinner size="sm" color="fg.brand" ml="auto" />}
    </chakra.button>
  );
}

function StatusChip({
  icon,
  label,
}: {
  icon: "spark-filled" | "check";
  label: string;
}) {
  return (
    <Flex
      align="center"
      gap="1"
      fontSize="xs"
      fontWeight="semibold"
      color="fg.brand"
      bg="bg.canvas"
      px="2"
      py="1"
      borderRadius="full"
      flexShrink={0}
    >
      <Icon name={icon} boxSize="10px" />
      {label}
    </Flex>
  );
}

export function EventRow({
  event,
  sparkName,
  alreadyAttending,
  selected,
  onPick,
}: {
  event: EligibleEvent;
  sparkName: string;
  alreadyAttending?: boolean;
  selected?: boolean;
  onPick: () => void;
}) {
  const label = event.title || event.activity;
  const disabled = !!event.disabledReason;
  return (
    <chakra.button
      type="button"
      onClick={disabled ? undefined : onPick}
      disabled={disabled}
      aria-disabled={disabled}
      display="flex"
      alignItems="center"
      gap="0.75rem"
      w="100%"
      textAlign="left"
      bg={selected ? "bg.brandSoft" : "transparent"}
      border="1px solid"
      borderColor={selected ? "border.brand" : "border.subtle"}
      borderRadius="md"
      px="0.75rem"
      py="0.625rem"
      cursor={disabled ? "not-allowed" : "pointer"}
      opacity={disabled ? 0.55 : 1}
      _hover={disabled || selected ? undefined : { bg: "bg.subtle" }}
    >
      {event.image && (
        <Box
          w="3rem"
          h="3rem"
          borderRadius="md"
          flexShrink={0}
          bg="bg.subtle"
          backgroundImage={`url(${event.image})`}
          backgroundSize="cover"
          backgroundPosition="center"
          filter={disabled ? "grayscale(1)" : undefined}
        />
      )}
      <Flex direction="column" flex="1" minW="0">
        <Text fontWeight="semibold" truncate>
          {label}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {formatEventWhen(event.startsAt)}
        </Text>
        {event.disabledReason && (
          <Text fontSize="xs" color="fg.danger" mt="0.5" fontWeight="medium">
            {event.disabledReason}
          </Text>
        )}
      </Flex>
      <Flex direction="column" align="flex-end" gap="1" flexShrink={0}>
        {event.viewerRegistered && (
          <StatusChip icon="check" label="You're going" />
        )}
        {alreadyAttending && (
          <StatusChip icon="spark-filled" label={`${sparkName} is going`} />
        )}
      </Flex>
    </chakra.button>
  );
}

/**
 * Read-only status shown when the viewer already has an active invite out to
 * this spark — pending (awaiting a response) or accepted (they're going). Only
 * one active invite per spark is allowed, so the picker is replaced by this.
 */
export function InvitePendingStatus({
  firstName,
  event,
}: {
  firstName: string;
  event: {
    title: string | null;
    activity: string;
    startsAt: string;
    image?: string;
    status: "pending" | "accepted";
  };
}) {
  const accepted = event.status === "accepted";
  return (
    <Flex direction="column" gap="0.75rem" align="center" py="0.5rem">
      <Flex
        w="2.5rem"
        h="2.5rem"
        borderRadius="full"
        bg="bg.brandSoft"
        color="fg.brand"
        align="center"
        justify="center"
      >
        <Icon name="check" boxSize="16px" />
      </Flex>
      <Text textStyle="caption" textAlign="center">
        {accepted ? `${firstName} is going` : `You've invited ${firstName}`}
      </Text>
      <Flex
        w="100%"
        align="center"
        gap="0.75rem"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        px="0.75rem"
        py="0.625rem"
      >
        {event.image && (
          <Box
            w="3rem"
            h="3rem"
            borderRadius="md"
            flexShrink={0}
            bg="bg.subtle"
            backgroundImage={`url(${event.image})`}
            backgroundSize="cover"
            backgroundPosition="center"
          />
        )}
        <Box minW="0">
          <Text textStyle="caption" truncate>
            {event.title || event.activity}
          </Text>
          <Text textStyle="inputLabel" color="fg.muted">
            {formatEventWhen(event.startsAt)}
          </Text>
        </Box>
      </Flex>
      <Text textStyle="inputLabel" color="fg.muted" textAlign="center">
        {accepted
          ? `${firstName} accepted — see you both there!`
          : `Waiting for ${firstName} to respond.`}
      </Text>
    </Flex>
  );
}

export function InviteSentConfirmation({
  firstName,
  onDone,
}: {
  firstName: string;
  onDone: () => void;
}) {
  return (
    <Flex direction="column" align="center" gap="0.75rem" py="1rem">
      <Flex
        w="3rem"
        h="3rem"
        borderRadius="full"
        bg="bg.brand"
        color="fg.onBrand"
        align="center"
        justify="center"
      >
        <Icon name="check" boxSize="18px" />
      </Flex>
      <Text fontWeight="semibold">Invite sent to {firstName}</Text>
      <Text fontSize="sm" color="fg.muted" textAlign="center">
        We'll let you know when they respond.
      </Text>
      <Button mt="0.5rem" onClick={onDone}>
        Done
      </Button>
    </Flex>
  );
}

/**
 * Sticky-bottom Send-invite footer. Caller controls visibility via `show`
 * — the SparkActionsDrawer hides it when the eligible-events list is
 * empty; InviteSparkFlow always shows it (with a disabled button) so the
 * user gets a clear "no event picked" affordance.
 */
export function SendInviteFooter({
  selected,
  sparkEventSparkId,
  firstName,
  sending,
  onSend,
  onRegister,
  onJoin,
  /** When false, the contextual copy line above the button is suppressed
   * — InviteSparkFlow only shows it when not in fixedEvent mode. */
  showCopy,
  show,
  /** Why the invitee can't take a spot at the selected event (age/gender
   * capacity), if any. When set, Send is disabled and the reason is shown, so
   * the invite can't be sent to an event that would reject the invitee. */
  disabledReason,
}: {
  selected: EligibleEvent | null;
  sparkEventSparkId: number | undefined;
  firstName: string;
  sending: boolean;
  onSend: () => void;
  /** Called when the viewer needs to register for `selected` before inviting. */
  onRegister: () => void;
  /** Called when the invitee is ALREADY going to `selected` and the viewer
   * isn't — the viewer registers to join them, no invite is sent. */
  onJoin: () => void;
  showCopy: boolean;
  show: boolean;
  disabledReason?: string;
}) {
  if (!show) return null;
  // When the invitee is already going to the picked event, you don't invite
  // them (that just fails ALREADY_REGISTERED) — you join them:
  //   - viewer not registered → "Register to join {name}" (no invite sent),
  //   - viewer already registered → you're both going, nothing to do.
  const inviteeAlreadyGoing =
    !!selected && selected.sparkId === sparkEventSparkId;
  const bothGoing = inviteeAlreadyGoing && !!selected?.viewerRegistered;
  const joinThem = inviteeAlreadyGoing && !selected?.viewerRegistered;
  // For a normal invite target the viewer isn't registered for, they must grab
  // their own spot before the invite can be sent (server-side NOT_REGISTERED).
  const needsRegistration =
    !!selected && !inviteeAlreadyGoing && !selected.viewerRegistered;

  const label = bothGoing
    ? "You're both going"
    : joinThem
      ? `Register to join ${firstName}`
      : needsRegistration
        ? "Sign up for this event"
        : "Send invite";
  const onClick = joinThem
    ? onJoin
    : needsRegistration
      ? onRegister
      : onSend;
  const copy = bothGoing
    ? `You're both going to this event.`
    : joinThem
      ? `${firstName} is already going — grab your spot to join them.`
      : needsRegistration
        ? `Grab your own spot first, then we'll invite ${firstName} to join you.`
        : `We'll let ${firstName} know you've invited them.`;

  return (
    <Box
      position="sticky"
      bottom="-1rem"
      mx="-1rem"
      mb="-1rem"
      px="1rem"
      pt="0.75rem"
      pb="1rem"
      bg="bg.canvas"
      borderTop="1px solid"
      borderColor="border.subtle"
      display="flex"
      flexDirection="column"
      gap="0.5rem"
    >
      {disabledReason ? (
        <Text fontSize="sm" color="fg.danger" textAlign="center">
          {disabledReason}
        </Text>
      ) : (
        showCopy &&
        selected && (
          <Text fontSize="sm" color="fg.muted" textAlign="center">
            {copy}
          </Text>
        )
      )}
      <Button
        w="100%"
        bg="bg.brand"
        color="fg.onBrand"
        onClick={onClick}
        loading={sending}
        disabled={!selected || sending || !!disabledReason || bothGoing}
      >
        {label}
      </Button>
    </Box>
  );
}
